/**
 * 재사용 가능한 모달 다이얼로그 플러그인
 * 백엔드 연동 가능한 범용 모달 시스템
 *
 * 사용 예시:
 *
 * 1. 기본 사용 (타이틀 + 컨텐츠)
 * <div x-data="simpleModal">
 *     <button @click="open('제목', '내용')">열기</button>
 * </div>
 *
 * 2. 옵션 사용
 * <div x-data="simpleModal">
 *     <button @click="open('제목', null, { size: 'lg', closeOnBackdrop: false })">열기</button>
 * </div>
 *
 * 3. 백엔드 연동
 * <div x-data="{
 *     modal: simpleModal,
 *     async loadData() {
 *         this.modal.setLoading(true)
 *         const data = await fetch('/api/data').then(r => r.json())
 *         this.data = data
 *         this.modal.setLoading(false)
 *     }
 * }">
 *
 * @returns {{
 *     isOpen: boolean,
 *     title: string,
 *     content: string,
 *     loading: boolean,
 *     size: string,
 *     closeOnBackdrop: boolean,
 *     closeOnEscape: boolean,
 *     onClose: function|null,
 *     open: function,
 *     close: function,
 *     setLoading: function,
 *     setTitle: function
 * }}
 */
export default () => ({
    // 상태
    isOpen: false,
    title: '',
    content: '',
    loading: false,

    // 설정 옵션
    size: 'md',                  // 'sm', 'md', 'lg', 'xl', 'full'
    closeOnBackdrop: true,       // 배경 클릭시 닫기
    closeOnEscape: true,         // ESC 키로 닫기
    onClose: null,               // 닫힐 때 실행할 콜백

    /**
     * 모달 열기
     * @param {string} title - 모달 제목
     * @param {string|null} content - 모달 내용 (선택사항, null이면 슬롯 사용)
     * @param {Object} options - 모달 옵션
     * @param {string} options.size - 모달 크기 ('sm', 'md', 'lg', 'xl', 'full')
     * @param {boolean} options.closeOnBackdrop - 배경 클릭시 닫기 여부
     * @param {boolean} options.closeOnEscape - ESC 키로 닫기 여부
     * @param {function} options.onClose - 닫힐 때 실행할 콜백
     */
    open(title = '', content = null, options = {}) {
        this.title = title;
        this.content = content;

        // 옵션 적용
        if (options.size) this.size = options.size;
        if (options.closeOnBackdrop !== undefined) this.closeOnBackdrop = options.closeOnBackdrop;
        if (options.closeOnEscape !== undefined) this.closeOnEscape = options.closeOnEscape;
        if (options.onClose) this.onClose = options.onClose;

        this.isOpen = true;
        this.loading = false; // 외부에서 조작해도 여기서 다시 무효가 된다! setLoading() 을 통해서만 조작해야 함

        // 트랜지션을 위한 약간의 지연
        this.$nextTick(() => {
            document.body.style.overflow = 'hidden'; // 스크롤 방지
        });
    },

    /**
     * 모달 닫기
     */
    close() {
        // onClose 콜백 실행
        if (typeof this.onClose === 'function') {
            this.onClose();
        }

        // 초기화 + 스크롤 복원 + 닫기
        this.$nextTick(() => {
            // 상태 초기화
            this.loading = false;
            this.content = '';

            // 옵션 초기화
            this.size = 'md';
            this.closeOnBackdrop = true;
            this.closeOnEscape = true;
            this.onClose = null;

            this.isOpen = false;
            document.body.style.overflow = ''; // 스크롤 복원
        });
    },

    /**
     * 배경 클릭 핸들러
     */
    handleBackdropClick() {
        if (this.closeOnBackdrop) {
            this.close();
        }
    },

    /**
     * ESC 키 핸들러
     */
    handleEscape() {
        if (this.closeOnEscape) {
            this.close();
        }
    },

    /**
     * 로딩 상태 설정
     * @param {boolean} loading - 로딩 여부
     */
    setLoading(loading) {
        this.loading = loading;
    },

    /**
     * 제목 변경
     * @param {string} title - 새 제목
     */
    setTitle(title) {
        this.title = title;
    },

    /**
     * 모달 크기에 따른 CSS 클래스 반환
     * @returns {string}
     */
    getSizeClass() {
        const sizeMap = {
            'sm': 'max-w-sm',
            'md': 'max-w-md',
            'lg': 'max-w-lg',
            'xl': 'max-w-xl',
            '2xl': 'max-w-2xl',
            '3xl': 'max-w-3xl',
            '4xl': 'max-w-4xl',
            '5xl': 'max-w-5xl',
            '6xl': 'max-w-6xl',
            '7xl': 'max-w-7xl',
            'full': 'max-w-full mx-4'
        };
        return sizeMap[this.size] || sizeMap['md'];
    }
});
